'use client';

/**
 * usePatchProfile — PATCH /api/v1/users/me.
 *
 * The caller's own row: name, profession, phone, job title, jurisdiction
 * preference, language. Not admin-gated, because it cannot reach anyone
 * else — org fields go through {@link usePatchOrgProfile}, which is a
 * different endpoint with a different gate and its own §1A refusals.
 *
 * This endpoint is new (2026-09-09). Before it, the web had *no* user-level
 * profile write at all: `full_name` was reachable only through the
 * admin-gated `PATCH /team/users/{id}`, and `profession` — which orders the
 * home grid and drives persona copy — was fixed at signup forever. The
 * backend shares `app/services/user_profile.py` with `PATCH /mobile/v1/me`,
 * so the two surfaces validate and write identically.
 *
 * **Send only what changed.** The server applies `exclude_unset`, so an
 * absent key is untouched and an explicit `''` clears. A form that PATCHes
 * its whole state nulls fields the user never looked at — the Org Profile
 * data-loss bug (API audit 2026-08-25, Finding 1). Callers diff against the
 * snapshot they seeded from; see `ProfileSection`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/lib/api';

import { USERS_ME_QUERY_KEY } from './useUsersMe';

export interface ProfilePatch {
  full_name?: string;
  profession?: string | null;
  phone?: string | null;
  job_title?: string | null;
  /** ISO country or subdivision code ("IN", "GB", "US-CA"). `''` clears. */
  preferred_jurisdiction?: string | null;
  preferred_language?: string;
}

export function usePatchProfile() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, ProfilePatch>({
    mutationFn: (body) => api<unknown>('/users/me', { method: 'PATCH', body }),
    // Invalidate rather than seed the cache from the response: useUsersMe
    // merges /users/me with /auth/me, and this reply is only the first half.
    // Writing it in directly would drop the auth-owned overlay fields.
    //
    // **Returned, not fired and forgotten.** react-query awaits a promise
    // from `onSuccess` before running the caller's own `onSuccess`, so a form
    // that clears its draft there is guaranteed to be looking at refetched
    // data — without this the fields flash back to their pre-save values for
    // the length of the round trip.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY }),
  });
}
